import * as React from "react";
import {
  OutlinedInput,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  Divider,
  Button,
} from "@mui/material";
import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import OutlinedCheckbox from "../../Forms/OutlinedCheckbox";
import { setupService } from "../../../Core/Api/services";
import { StorageKind } from "../../../Core/Api/Types/storage-kind";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import { DbLabProviderInfos } from "../../../Core/Api/Types/db-lab-provider-infos";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";

export interface ProviderEditPanelProps {
  width?: number;
  open: boolean;
  provider: DbLabProviderInfos;
  header?: string;
  onClose?: React.MouseEventHandler;
}

const ProviderEditPanel: React.FC<ProviderEditPanelProps> = (props) => {
  const { t } = useTranslation();
  const { provider } = props;
  const [storages, setStorages] = React.useState<DbStorageInfos[]>([]);
  const [selectedStorageId, setSelectedStorageId] = React.useState<number>();
  const [newName, setNewName] = React.useState<string>();
  const [newDesc, setNewDesc] = React.useState<string>();
  const [newEmail, setNewEmail] = React.useState<string>();
  const { active: affinityCheck, toggle: toggleAffinityCheck } = useToggle(props.provider?.disableAffinityCheck!);
  const customSnackbar = useCustomSnackbar();

  React.useEffect(() => {
    setupService.GetAllStorages().then((response) => {
      let ingestStorages = response.storages?.filter((sto) => sto.kind === StorageKind.Ingest);
      if (ingestStorages) setStorages(ingestStorages);
    });
  }, []);

  React.useEffect(() => {
    let defaultStoId = storages.find((sto) => sto.id === provider.storageId)?.id;
    setSelectedStorageId(defaultStoId);
    setNewName(provider?.labName || "");
    setNewDesc(provider?.labDescription || "");
    setNewEmail(provider?.labEmail || "");
  }, [provider]);

  const handleConfirmEdit = () => {
    let newProvider = provider;
    newProvider.labName = newName || "";
    newProvider.labEmail = newEmail || "";
    newProvider.labDescription = newDesc || "";
    newProvider.disableAffinityCheck = affinityCheck;
    newProvider.storageId = selectedStorageId || 0;

    setupService.UpdateProvider(provider).then((response) => {
      if (response.success) customSnackbar(t("alert:success.providerUpdated"), "success");
      else customSnackbar(response.message ?? t("alert:error.updatingProvider"), "error");
    });
  };

  return (
    <Panel
      header={
        <PanelHeader
          title={
            props.provider
              ? t("panel:title.editProviderWithName", { name: props.provider.labName })
              : t("panel:title.editProvider")
          }
          onClose={props.onClose as PanelHeaderProps["onClose"]}
        />
      }
      footerContent={
        <Button color="primary" variant="contained" fullWidth onClick={handleConfirmEdit}>
          {t("button:confirmChanges")}
        </Button>
      }
      open={props.open}
      onClose={props.onClose}
    >
      <Box>
        <FormControl sx={{ width: "100%", mb: 2 }}>
          <InputLabel shrink htmlFor="customer-name">
            {t("common:providerName")}
          </InputLabel>
          <OutlinedInput id="customer-name" value={newName} onChange={(e) => setNewName(e.currentTarget.value)} />
        </FormControl>
        <FormControl sx={{ width: "100%", mb: 2 }}>
          <InputLabel shrink htmlFor="external-id" sx={{ width: "100%" }}>
            {t("common:description")}
          </InputLabel>
          <OutlinedInput
            label={t("common:standard")}
            sx={{ flex: 1 }}
            value={newDesc}
            onChange={(e) => setNewDesc(e.currentTarget.value)}
          />
        </FormControl>
        <FormControl sx={{ width: "100%" }}>
          <InputLabel shrink htmlFor="email" sx={{ width: "100%" }}>
            {t("common:email")}
          </InputLabel>
          <OutlinedInput
            label={t("common:standard")}
            sx={{ flex: 1 }}
            value={newEmail}
            onChange={(e) => setNewEmail(e.currentTarget.value)}
          />
        </FormControl>

        <OutlinedCheckbox checked={affinityCheck} mt={2.5} onClick={toggleAffinityCheck}>
          <Typography variant="body2" fontWeight={500}>
            {t("common:disableAffinityCheck")}
          </Typography>
        </OutlinedCheckbox>

        <Divider sx={{ mt: 3, mb: 3 }} />

        {storages && (
          <Autocomplete
            value={
              storages.find((sto) => sto.id === selectedStorageId)
                ? storages.find((sto) => sto.id === selectedStorageId)
                : null
            }
            sx={{ mt: 2 }}
            onChange={(e, value) => setSelectedStorageId(value?.id as number)}
            options={storages}
            getOptionLabel={(value: any) => value.name}
            disabled={!UserHasAcl(AclTypes.aclSuperUser)}
            renderInput={(params) => <TextField label={t("field:label.selectLinkedStorage")} {...params} />}
          />
        )}
      </Box>
    </Panel>
  );
};

export default ProviderEditPanel;
